import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import clsx from "clsx";

import { View, Button } from "../components/base";
import { LeftNavbar, NavItem } from "../components/LeftNavbar";
import { RequireAuth } from "../components/auth/RequireAuth";
import { Icon } from "../components/base/Icon";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { useAuth } from "../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../redux";
import { organizationMembersActions } from "../redux/reducers/organizationMemberSlice";
import { CommandPaletteProvider, CommandPalette } from "../components/CommandPalette";
import { LowBalanceBanner } from "../components/LowBalanceBanner";
import { EmailVerificationScreen } from "../components/auth/EmailVerificationScreen";

export const StandardLayout = () => {
  const dispatch = useAppDispatch();
  const { user, isAdmin } = useAuth();
  const { organization, organizationId, isUsingSelectedOrg } = useEffectiveOrganization();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Save to localStorage whenever collapsed state changes
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Auto-collapse sidebar when debugger is open
  useEffect(() => {
    const handleDebuggerCollapse = () => {
      const isOnFunctionPage = location.pathname.includes("/function");
      if (!isOnFunctionPage) return;

      const isDebuggerOpen = document.querySelector('[aria-label*="Collapse debug sidebar"]') !== null;

      if (isDebuggerOpen && !isSidebarCollapsed) {
        // Store original state before auto-collapsing
        if (!localStorage.getItem("autoCollapsedForDebugger")) {
          localStorage.setItem("autoCollapsedForDebugger", "true");
          localStorage.setItem("originalSidebarState", JSON.stringify(isSidebarCollapsed));
        }
        setIsSidebarCollapsed(true);
      } else if (!isDebuggerOpen && localStorage.getItem("autoCollapsedForDebugger")) {
        // Restore original state when debugger closes
        const originalState = localStorage.getItem("originalSidebarState");
        if (originalState !== null) {
          setIsSidebarCollapsed(JSON.parse(originalState));
          localStorage.removeItem("autoCollapsedForDebugger");
          localStorage.removeItem("originalSidebarState");
        }
      }
    };

    // Check on mount
    handleDebuggerCollapse();

    // Use MutationObserver to detect when debugger opens/closes
    const observer = new MutationObserver(() => {
      // Add a small delay to ensure DOM changes are complete
      setTimeout(handleDebuggerCollapse, 100);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-label"],
    });

    return () => {
      observer.disconnect();
    };
  }, [location.pathname, isSidebarCollapsed]);

  // Fetch org members early so we know the user's role for billing nav visibility
  useEffect(() => {
    if (organizationId) {
      dispatch(organizationMembersActions.get({ organizationId }));
    }
  }, [organizationId]);

  const organizationMembers = useAppSelector((state) => Object.values(state.organizationMembers.entities));
  const currentMember = organizationMembers.find((member) => member.userId === user?.id);
  const isOrgAdminOrOwner = currentMember?.role === "admin" || currentMember?.role === "owner";
  const showBillingLink = organization?.billingEnabled && (isAdmin || isOrgAdminOrOwner);

  const sidebarNavItems: NavItem[] = [
    { label: "Home", to: "/", icon: "home" },
    { label: "Agents", to: "/agents", icon: "bot" },
    {
      label: "Tooling",
      icon: "construction",
      children: [{ label: "Evaluators", to: "/evaluators" }, ...(isAdmin ? [{ label: "Simulations", to: "/agents/simulations" }] : [])],
    },
    {
      label: "Organization",
      icon: "users",
      children: [
        { label: "Members", to: "/organization/members" },
        { label: "Configurations", to: "/organization/configurations" },
        { label: "Usage", to: "/organization/usage" },
        ...(showBillingLink ? [{ label: "Billing", to: "/organization/billing" }] : []),
      ],
    },
    {
      label: "Resources",
      icon: "folder_open",
      children: [
        { label: "Voice Library", to: "/voice-library" },
        { label: "STT Lab", to: "/sttlab" },
        { label: "Documentation", externalUrl: "https://docs.voicerun.com" },
      ],
    },
    ...(isAdmin ? [{ label: "Admin", to: "/admin", icon: "admin_panel_settings" }] : []),
  ];

  return (
    <CommandPaletteProvider>
      <RequireAuth>
        {user?.emailVerified === false ? (
          <EmailVerificationScreen />
        ) : (
          <View className="relative flex w-full h-screen bg-gray-50 dark:bg-black">
            <LeftNavbar
              navItems={sidebarNavItems}
              isOpen={isMobileMenuOpen}
              onClose={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
              isAdminViewingOtherOrg={isUsingSelectedOrg}
              onToggleCollapse={() => {
                const isOnFunctionPage = location.pathname.includes("/function");
                const isDebuggerOpen = document.querySelector('[aria-label*="Collapse debug sidebar"]') !== null;

                // If trying to expand sidebar while debugger is open on function page, close debugger instead
                if (isOnFunctionPage && isDebuggerOpen && isSidebarCollapsed) {
                  // Find and click the debugger close button
                  const debuggerCloseButton = document.querySelector('[aria-label*="Collapse debug sidebar"]');
                  if (debuggerCloseButton) {
                    (debuggerCloseButton as HTMLElement).click();
                  }
                } else {
                  // Normal toggle behavior
                  setIsSidebarCollapsed(!isSidebarCollapsed);
                }
              }}
            />
            <View className={clsx("flex flex-col flex-1 min-w-0", isSidebarCollapsed ? "lg:ml-[60px]" : "lg:ml-[220px]")}>
              {/* Mobile hamburger button */}
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsMobileMenuOpen(true)}
                className="hamburger-button lg:hidden absolute top-4 left-4 z-40 !h-10 !w-10 rounded-md bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#1a1a1a] hover:bg-gray-100 dark:hover:bg-[#0a0a0a]/80 transition-colors flex items-center justify-center shadow-md"
              >
                <Icon
                  name="menu"
                  size={20}
                  className="text-gray-600 dark:text-[#666]"
                />
              </Button>
              <View className="flex-1 h-full bg-gray-50 dark:bg-black min-w-0 flex flex-col">
                <LowBalanceBanner />
                <Outlet />
              </View>
            </View>
          </View>
        )}
        <CommandPalette />
      </RequireAuth>
    </CommandPaletteProvider>
  );
};
